/**
 * lib/experiences/knowledgeVisuals.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Visual metadata for Sift Trip Local knowledge items.
 *
 * Design principle: editorial, magazine-style (Monocle / AFAR / Condé Nast).
 * Accents are muted, not loud. Backgrounds barely-there.
 * Icons + labels ensure accessibility — never color-only distinction.
 *
 * Each entry maps a knowledge_category → visual identity used in ExperienceRow.
 *
 * Categories:
 *   hidden_gem | neighborhood | cafe | market | viewpoint | park | local_experience
 */

// ─── Type ─────────────────────────────────────────────────────────────────────

export interface KnowledgeVisual {
  /** Legacy display symbol. UI renders lucide icons in the itinerary. */
  icon:   string;
  /** Human-readable label in Spanish */
  label:  string;
  /** OKLCH accent — used for badge text, left border, icon tint */
  accent: string;
  /** Very subtle background tint for the row */
  bg:     string;
  /** Left-border accent — slightly more opaque than bg */
  border: string;
}

// ─── Category → visual mapping ────────────────────────────────────────────────

export const KNOWLEDGE_VISUALS: Readonly<Record<string, KnowledgeVisual>> = {
  hidden_gem: {
    icon:   "◇",
    label:  "Rincón local",
    accent: "oklch(0.60 0.09 70)",
    bg:     "oklch(0.716 0.082 74 / 0.055)",
    border: "oklch(0.716 0.082 74 / 0.48)",
  },
  neighborhood: {
    icon:   "•",
    label:  "Barrio recomendado",
    accent: "oklch(0.50 0.11 248)",
    bg:     "oklch(0.52 0.11 248 / 0.045)",
    border: "oklch(0.52 0.11 248 / 0.36)",
  },
  cafe: {
    icon:   "•",
    label:  "Café recomendado",
    accent: "oklch(0.48 0.09 54)",
    bg:     "oklch(0.50 0.09 54 / 0.055)",
    border: "oklch(0.50 0.09 54 / 0.42)",
  },
  market: {
    icon:   "•",
    label:  "Mercado local",
    accent: "oklch(0.56 0.13 50)",
    bg:     "oklch(0.58 0.13 50 / 0.055)",
    border: "oklch(0.58 0.13 50 / 0.42)",
  },
  viewpoint: {
    icon:   "•",
    label:  "Mirador",
    accent: "oklch(0.52 0.10 260)",
    bg:     "oklch(0.55 0.10 260 / 0.045)",
    border: "oklch(0.55 0.10 260 / 0.36)",
  },
  park: {
    icon:   "•",
    label:  "Parque local",
    accent: "oklch(0.42 0.11 148)",
    bg:     "oklch(0.44 0.11 148 / 0.05)",
    border: "oklch(0.44 0.11 148 / 0.40)",
  },
  local_experience: {
    icon:   "✦",
    label:  "Experiencia local",
    accent: "oklch(0.58 0.08 72)",
    bg:     "oklch(0.716 0.082 74 / 0.04)",
    border: "oklch(0.716 0.082 74 / 0.34)",
  },
};

/** Fallback for unknown or future categories — neutral gold */
export const KNOWLEDGE_VISUAL_FALLBACK: KnowledgeVisual = {
  icon:   "✦",
  label:  "Sift Trip Local",
  accent: "oklch(0.58 0.08 72)",
  bg:     "oklch(0.716 0.082 74 / 0.04)",
  border: "oklch(0.716 0.082 74 / 0.30)",
};

/**
 * Resolve visual metadata for a knowledge item.
 * Always returns a valid KnowledgeVisual — falls back to KNOWLEDGE_VISUAL_FALLBACK.
 */
export function resolveKnowledgeVisual(category: string | null | undefined): KnowledgeVisual {
  if (!category) return KNOWLEDGE_VISUAL_FALLBACK;
  return KNOWLEDGE_VISUALS[category] ?? KNOWLEDGE_VISUAL_FALLBACK;
}
